import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';
import {
  PenLine, Save, Lock, Plus, Trash2, Loader2, Trophy, LayoutTemplate,
  Copy, Check, MessageSquareQuote, FileStack,
} from 'lucide-react';

const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().slice(0, 10);
const ESTADOS = [
  { v: 'borrador', label: 'Borrador', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  { v: 'listo', label: 'Listo', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { v: 'usado', label: 'Usado', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
];

export default function CopiesSection({ clientId }) {
  const [data, setData] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getCopies(clientId);
      setData(res?.data || {});
      setCanEdit(!!res?.can_edit);
      setDirty(false);
    } catch { toast.error('Error cargando los copies'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setList = (key, next) => { setData((p) => ({ ...p, [key]: next })); setDirty(true); };
  const setTono = (patch) => { setData((p) => ({ ...p, tono: { ...(p.tono || {}), ...patch } })); setDirty(true); };
  const get = (key) => Array.isArray(data?.[key]) ? data[key] : [];

  const handleSave = async () => {
    setSaving(true);
    try { await serviceDeliveryAPI.saveCopies(clientId, data); toast.success('Copies guardados'); await load(); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const copyText = (t, id) => {
    if (!t) return;
    navigator.clipboard?.writeText(t).then(() => { setCopied(id); toast.success('Copiado'); setTimeout(() => setCopied(''), 1200); }).catch(() => toast.error('No se pudo copiar'));
  };

  if (loading || !data) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const RO = !canEdit;
  const tono = data.tono || {};

  const Section = ({ icon: Icon, title, desc, k, onAdd, children }) => (
    <Card data-testid={`copies-${k}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Icon className="w-4 h-4 text-primary" /> {title}</CardTitle>
            {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
          </div>
          {!RO && onAdd && <Button size="sm" variant="outline" onClick={onAdd} data-testid={`copies-add-${k}`}><Plus className="w-4 h-4 mr-1" /> Añadir</Button>}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
  const empty = (t) => <p className="text-sm text-muted-foreground italic">{t}</p>;

  return (
    <div className="space-y-5" data-testid="copies-section">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><PenLine className="w-5 h-5 text-primary" /> Copies</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">El espacio del copywriter: el texto escrito de este cliente.</p>
            </div>
            {RO ? <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge> : (
              <Button onClick={handleSave} disabled={saving || !dirty} data-testid="copies-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{dirty ? 'Guardar' : 'Guardado'}
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* 1) COPIES GANADORES */}
      <Section icon={Trophy} title="Copies ganadores" desc="Los textos que mejor han funcionado, para reutilizar la estructura." k="ganadores"
        onAdd={() => setList('ganadores', [...get('ganadores'), { id: uid(), texto: '', tipo_publicacion: '', nota: '', fecha: today() }])}>
        {get('ganadores').length === 0 ? empty('Sin copies ganadores.') : (
          <div className="space-y-3">
            {get('ganadores').map((g) => (
              <div key={g.id} className="rounded-lg border p-3 space-y-2" data-testid={`copies-ganador-${g.id}`}>
                <div className="flex items-start gap-2">
                  <Textarea value={g.texto} onChange={(e) => setList('ganadores', get('ganadores').map((x) => x.id === g.id ? { ...x, texto: e.target.value } : x))} disabled={RO} rows={4} placeholder="El texto / pie de foto" className="resize-y font-mono text-sm" data-testid={`copies-ganador-texto-${g.id}`} />
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => copyText(g.texto, g.id)} title="Copiar">{copied === g.id ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</Button>
                    {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setList('ganadores', get('ganadores').filter((x) => x.id !== g.id))}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input value={g.tipo_publicacion} onChange={(e) => setList('ganadores', get('ganadores').map((x) => x.id === g.id ? { ...x, tipo_publicacion: e.target.value } : x))} disabled={RO} placeholder="Tipo de publicación" className="h-8 text-xs" />
                  <Input value={g.nota} onChange={(e) => setList('ganadores', get('ganadores').map((x) => x.id === g.id ? { ...x, nota: e.target.value } : x))} disabled={RO} placeholder="Por qué funcionó" className="h-8 text-xs" />
                  <Input type="date" value={g.fecha || ''} onChange={(e) => setList('ganadores', get('ganadores').map((x) => x.id === g.id ? { ...x, fecha: e.target.value } : x))} disabled={RO} className="h-8 text-xs" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 2) PLANTILLAS DE COPY */}
      <Section icon={LayoutTemplate} title="Plantillas de copy" desc="Estructuras reutilizables (ej: gancho + desarrollo + llamada a la acción)." k="plantillas"
        onAdd={() => setList('plantillas', [...get('plantillas'), { id: uid(), nombre: '', estructura: '' }])}>
        {get('plantillas').length === 0 ? empty('Sin plantillas.') : (
          <div className="space-y-2">
            {get('plantillas').map((p) => (
              <div key={p.id} className="rounded-lg border p-3 space-y-2" data-testid={`copies-plantilla-${p.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={p.nombre} onChange={(e) => setList('plantillas', get('plantillas').map((x) => x.id === p.id ? { ...x, nombre: e.target.value } : x))} disabled={RO} placeholder="Nombre de la plantilla" className="h-8 font-medium" data-testid={`copies-plantilla-nombre-${p.id}`} />
                  {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setList('plantillas', get('plantillas').filter((x) => x.id !== p.id))}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <Textarea value={p.estructura} onChange={(e) => setList('plantillas', get('plantillas').map((x) => x.id === p.id ? { ...x, estructura: e.target.value } : x))} disabled={RO} rows={3} placeholder="Gancho → desarrollo → CTA…" className="resize-y font-mono text-sm" />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 3) FRASES Y CTAs */}
      <Section icon={MessageSquareQuote} title="Frases y CTAs de la marca" desc="CTAs habituales, expresiones propias, hashtags fijos. Cópialos de un clic." k="frases_ctas"
        onAdd={() => setList('frases_ctas', [...get('frases_ctas'), { id: uid(), texto: '' }])}>
        {get('frases_ctas').length === 0 ? empty('Sin frases ni CTAs.') : (
          <div className="space-y-2">
            {get('frases_ctas').map((f) => (
              <div key={f.id} className="flex items-center gap-2" data-testid={`copies-frase-${f.id}`}>
                <Input value={f.texto} onChange={(e) => setList('frases_ctas', get('frases_ctas').map((x) => x.id === f.id ? { ...x, texto: e.target.value } : x))} disabled={RO} placeholder="Ej: 'Reserva ya tu cita 📅' o #JardínDenda" className="h-9" data-testid={`copies-frase-texto-${f.id}`} />
                <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => copyText(f.texto, f.id)} title="Copiar" data-testid={`copies-frase-copy-${f.id}`}>{copied === f.id ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</Button>
                {!RO && <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 shrink-0" onClick={() => setList('frases_ctas', get('frases_ctas').filter((x) => x.id !== f.id))}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 4) TONO DE ESCRITURA */}
      <Card data-testid="copies-tono">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><PenLine className="w-4 h-4 text-primary" /> Tono de escritura del cliente</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">La "chuleta" de cómo escribe esta marca, para no fallar el tono.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { k: 'registro', label: 'Registro (cercano / formal)' },
            { k: 'emojis', label: 'Emojis (sí / no / moderado)' },
            { k: 'tratamiento', label: 'Tratamiento (tuteo / usted)' },
            { k: 'palabras_usa', label: 'Palabras que usa' },
            { k: 'palabras_evita', label: 'Palabras que evita' },
            { k: 'notas', label: 'Notas de tono' },
          ].map((f) => (
            <div key={f.k} className={f.k === 'notas' ? 'sm:col-span-2' : ''}>
              <Label className="text-xs">{f.label}</Label>
              <Textarea value={tono[f.k] || ''} onChange={(e) => setTono({ [f.k]: e.target.value })} disabled={RO} rows={2} className="resize-none" data-testid={`copies-tono-${f.k}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* 5) BANCO DE COPIES */}
      <Section icon={FileStack} title="Banco de copies" desc="Textos ya escritos, listos o en borrador, para ir tirando." k="banco"
        onAdd={() => setList('banco', [{ id: uid(), texto: '', estado: 'borrador' }, ...get('banco')])}>
        {get('banco').length === 0 ? empty('Sin copies en el banco.') : (
          <div className="space-y-3">
            {get('banco').map((b) => {
              const est = ESTADOS.find((e) => e.v === b.estado) || ESTADOS[0];
              return (
                <div key={b.id} className="rounded-lg border p-3 space-y-2" data-testid={`copies-banco-${b.id}`}>
                  <div className="flex items-center gap-2">
                    {RO ? <Badge variant="outline" className={est.cls}>{est.label}</Badge> : (
                      <select value={b.estado} onChange={(e) => setList('banco', get('banco').map((x) => x.id === b.id ? { ...x, estado: e.target.value } : x))} className={`h-8 rounded-md border px-2 text-xs ${est.cls}`} data-testid={`copies-banco-estado-${b.id}`}>
                        {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
                      </select>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => copyText(b.texto, b.id)} title="Copiar">{copied === b.id ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</Button>
                      {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setList('banco', get('banco').filter((x) => x.id !== b.id))}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </div>
                  <Textarea value={b.texto} onChange={(e) => setList('banco', get('banco').map((x) => x.id === b.id ? { ...x, texto: e.target.value } : x))} disabled={RO} rows={5} placeholder="Escribe el copy tal cual se publicará (saltos de línea, emojis…)" className="resize-y font-mono text-sm" data-testid={`copies-banco-texto-${b.id}`} />
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
